import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from './Button';
import Icon from '../AppIcon';

const Header = ({ 
  user = null,
  title,
  subtitle,
  onLogin,
  onLogout,
  onUpgrade,
  showBackButton = false,
  className = ''
}) => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleNavigation = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const navItems = [
    { path: '/trading-dashboard', label: 'Dashboard', icon: 'TrendingUp' },
    { path: '/portfolio-tracker', label: 'Portfolio', icon: 'PieChart' },
    { path: '/ai-predictions-detail', label: 'AI Predictions', icon: 'Brain' },
    { path: '/trading-signals', label: 'Signals', icon: 'Zap' },
    { path: '/market-analysis', label: 'Analysis', icon: 'BarChart3' },
    { path: '/tax-calculator', label: 'Tax Calculator', icon: 'Calculator' }
  ];

  return (
    <header className={`bg-card border-b border-border shadow-sm sticky top-0 z-50 ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Left Section */}
          <div className="flex items-center space-x-4">
            {showBackButton && (
              <Button
                variant="ghost"
                size="sm"
                iconName="ArrowLeft"
                onClick={() => window.history?.back()}
                className="lg:hidden"
              />
            )}
            
            {/* Logo/Brand */}
            <div 
              className="flex items-center space-x-2 cursor-pointer"
              onClick={() => handleNavigation('/')}
            >
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="TrendingUp" size={20} className="text-primary-foreground" />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-lg font-bold text-foreground">
                  {title || 'CryptoPredict Pro'}
                </h1>
                {subtitle && (
                  <p className="text-sm text-muted-foreground">{subtitle}</p>
                )}
              </div>
            </div>
          </div>

          {/* Center Navigation - Desktop */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navItems?.map((item) => (
              <Button
                key={item?.path}
                variant="ghost"
                size="sm"
                iconName={item?.icon}
                onClick={() => handleNavigation(item?.path)}
                className="text-muted-foreground hover:text-foreground"
              >
                {item?.label}
              </Button>
            ))}
          </nav>

          {/* Right Section */}
          <div className="flex items-center space-x-3">
            {user ? (
              <div className="flex items-center space-x-3">
                {/* User Info - Desktop */}
                <div className="hidden md:flex items-center space-x-3">
                  <div className="text-right">
                    <div className="text-sm font-medium text-foreground">
                      {user?.name || user?.email}
                    </div>
                    <div className="text-xs text-muted-foreground capitalize">
                      {user?.subscriptionTier || 'Free'} Plan
                    </div>
                  </div>
                  
                  {user?.subscriptionTier !== 'pro' && onUpgrade && (
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Zap"
                      onClick={onUpgrade}
                      className="text-amber-600 border-amber-200 hover:bg-amber-50"
                    >
                      Upgrade
                    </Button>
                  )}
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="LogOut"
                    onClick={onLogout}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    Logout
                  </Button>
                </div>

                {/* User Avatar */}
                <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                  <Icon name="User" size={16} className="text-primary" />
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleNavigation('/user-registration')}
                  className="hidden sm:inline-flex"
                >
                  Sign Up
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  iconName="LogIn"
                  onClick={onLogin || (() => handleNavigation('/user-login'))}
                >
                  Login
                </Button>
              </div>
            )}

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="sm"
              iconName="Menu"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden"
            />
          </div>
        </div>
      </div>
      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-border bg-card">
          <div className="px-4 py-4 space-y-2">
            {navItems?.map((item) => (
              <Button
                key={item?.path}
                variant="ghost"
                size="sm"
                iconName={item?.icon}
                onClick={() => handleNavigation(item?.path)}
                className="w-full justify-start text-muted-foreground hover:text-foreground"
              >
                {item?.label}
              </Button>
            ))}
            
            {user && (
              <div className="pt-4 border-t border-border space-y-2">
                <div className="px-3 py-2">
                  <div className="text-sm font-medium text-foreground">
                    {user?.name || user?.email}
                  </div>
                  <div className="text-xs text-muted-foreground capitalize">
                    {user?.subscriptionTier || 'Free'} Plan
                  </div>
                </div>
                
                {user?.subscriptionTier !== 'pro' && onUpgrade && (
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Zap"
                    onClick={onUpgrade}
                    className="w-full justify-start text-amber-600 border-amber-200"
                  >
                    Upgrade Plan
                  </Button>
                )}
                
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="LogOut"
                  onClick={onLogout}
                  className="w-full justify-start text-muted-foreground hover:text-foreground"
                >
                  Logout
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;